import { Fragment, useCallback, useState } from "react"
import { Check, Play, Rocket, Trophy } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Table/Table"
import { Badge } from "../Badge/Badge"
import { OptimizerConfigRanges } from "../OptimizerConfigRanges/OptimizerConfigRanges"
import type { OptimizerRow } from "../types/optimizer"

export interface OptimizerTableProps {
  rows: OptimizerRow[]
  deployedRanks?: Set<number>
  expandedRanks?: Set<number>
  defaultExpandedRanks?: Set<number>
  onExpandedRanksChange?: (ranks: Set<number>) => void
  onRunBacktest?: (row: OptimizerRow) => void
  onDeployRow?: (row: OptimizerRow) => void
  onRowSelect?: (row: OptimizerRow) => void
  className?: string
}

const HEAD_BASE = "text-caption text-muted-foreground uppercase tracking-wider"
const NUM_CELL = "text-body text-muted-foreground text-right tabular-nums"
const WIDE_ONLY = "hidden 2xl:table-cell"

export function OptimizerTable({
  rows,
  deployedRanks,
  expandedRanks: controlledExpanded,
  defaultExpandedRanks,
  onExpandedRanksChange,
  onRunBacktest,
  onDeployRow,
  onRowSelect,
  className,
}: OptimizerTableProps) {
  const [internalExpanded, setInternalExpanded] = useState<Set<number>>(
    () => defaultExpandedRanks ?? new Set(),
  )
  const expanded = controlledExpanded ?? internalExpanded
  const showActions = !!onRunBacktest || !!onDeployRow

  const toggleRow = useCallback(
    (rank: number) => {
      const next = new Set(expanded)
      if (next.has(rank)) next.delete(rank)
      else next.add(rank)
      setInternalExpanded(next)
      onExpandedRanksChange?.(next)
    },
    [expanded, onExpandedRanksChange],
  )

  return (
    <Table className={cn("table-fixed", className)}>
      <TableHeader>
        <TableRow className="border-t border-border">
          <TableHead className={cn(HEAD_BASE, "text-right 2xl:text-left w-[13%] 2xl:w-[8%]")}>Rank</TableHead>
          <TableHead className={cn(HEAD_BASE, "text-right 2xl:text-left w-[29%] 2xl:w-[11%]")}>Return</TableHead>
          <TableHead className={cn(HEAD_BASE, "text-right w-[29%] 2xl:w-[10%]")}>Sharpe</TableHead>
          <TableHead className={cn(HEAD_BASE, "text-right 2xl:w-[9%]", WIDE_ONLY)}>PF</TableHead>
          <TableHead className={cn(HEAD_BASE, "text-right 2xl:text-left w-[29%] 2xl:w-[12%]")}>Max DD</TableHead>
          <TableHead className={cn(HEAD_BASE, "text-right 2xl:w-[11%]", WIDE_ONLY)}>Win Rate</TableHead>
          <TableHead className={cn(HEAD_BASE, "text-right 2xl:w-[9%]", WIDE_ONLY)}>Trades</TableHead>
          <TableHead className={cn(HEAD_BASE, "text-right 2xl:w-[15%]", WIDE_ONLY)}>Balance</TableHead>
          {showActions && (
            <TableHead className={cn("text-center 2xl:w-[15%]", WIDE_ONLY)} />
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const isFirst = r.rank === 1
          const isExpanded = expanded.has(r.rank)
          const returnPositive = r.returnPct >= 0
          const isDeployed = deployedRanks?.has(r.rank) ?? r.deployed ?? false
          return (
            <Fragment key={r.rank}>
              <TableRow
                onClick={() => {
                  onRowSelect?.(r)
                  toggleRow(r.rank)
                }}
                className="transition-colors hover:bg-muted/50 cursor-pointer"
              >
                <TableCell className="text-right 2xl:text-left">
                  <span className="inline-flex items-center gap-1.5">
                    {/* {isFirst && <Trophy size={12} className="text-primary" />} */}
                    <span className="text-body font-semibold tabular-nums text-foreground">
                      #{r.rank}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="text-right 2xl:text-left">
                  <Badge
                    variant="outline"
                    className={cn(
                      "font-semibold",
                      returnPositive ? "text-success" : "text-danger",
                    )}
                  >
                    {returnPositive ? "+" : ""}
                    {r.returnPct.toFixed(1)}%
                  </Badge>
                </TableCell>
                <TableCell className={cn(NUM_CELL, "text-foreground")}>
                  {r.sharpe.toFixed(2)}
                </TableCell>
                <TableCell className={cn(NUM_CELL, WIDE_ONLY)}>
                  {r.profitFactor.toFixed(2)}
                </TableCell>
                <TableCell className="text-right 2xl:text-left">
                  <Badge variant="outline" className="font-semibold text-danger">
                    −{Math.abs(r.maxDD).toFixed(1)}%
                  </Badge>
                </TableCell>
                <TableCell className={cn(NUM_CELL, WIDE_ONLY)}>
                  {r.winRate.toFixed(1)}%
                </TableCell>
                <TableCell className={cn(NUM_CELL, WIDE_ONLY)}>
                  {r.trades}
                </TableCell>
                <TableCell className={cn(NUM_CELL, WIDE_ONLY)}>
                  ${r.finalBalance.toLocaleString()}
                </TableCell>
                {showActions && (
                  <TableCell className={cn("text-center", WIDE_ONLY)}>
                    <span className="inline-flex items-center gap-3 align-middle">
                      {onRunBacktest && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRunBacktest(r)
                          }}
                          aria-label="Run backtest"
                          className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                        >
                          <Play size={12} fill="currentColor" strokeWidth={0} />
                        </button>
                      )}
                      {onDeployRow && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onDeployRow(r)
                          }}
                          disabled={isDeployed}
                          aria-label={isDeployed ? "Already deployed" : "Deploy"}
                          className={cn(
                            "transition-colors",
                            isDeployed
                              ? "text-foreground cursor-default"
                              : "text-muted-foreground hover:text-primary cursor-pointer",
                          )}
                        >
                          {isDeployed ? (
                            <Check size={12} strokeWidth={2.25} />
                          ) : (
                            <Rocket size={12} />
                          )}
                        </button>
                      )}
                    </span>
                  </TableCell>
                )}
              </TableRow>
              {isExpanded && (
                <TableRow className="bg-muted/30 hidden md:table-row">
                  <TableCell colSpan={showActions ? 9 : 8} className="py-3">
                    <OptimizerConfigRanges ranges={r.params} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}
